import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';

import { ApiEndpoints } from '../../core/api-endpoints.service';
import { AccessKey, WorkflowState } from '../../core/workflow-state.service';
import { ApiCallState } from '../../shared/api-call-state';
import {
  ApiEndpointComponent,
  ApiResultComponent,
} from '../../shared/api-result.component';

type ReportMode = 'event' | 'deviation';

const EVENTS = [
  'UNLOCK',
  'LOCK',
  'DELIVERED',
  'PICKEDUP',
  'DROPOFF_DELIVERED',
  'DROPOFF_PICKEDUP',
  'HANDOVER_DELIVERED',
  'HANDOVER_PICKEDUP',
] as const;

type ReportEvent = (typeof EVENTS)[number];

const DEVIATIONS = [
  'LockedSpaceNotAccessible',
  'UnableToLock',
  'UnableToUnlock',
  'AccessKeyNotAvailable',
  'ParcelNotFound',
] as const;

type ReportDeviation = (typeof DEVIATIONS)[number];

@Component({
  selector: 'app-access-report',
  imports: [FormsModule, ApiEndpointComponent, ApiResultComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './access-report.component.html',
})
export class AccessReportComponent {
  readonly mode = input.required<ReportMode>();

  private readonly http = inject(HttpClient);
  private readonly endpoints = inject(ApiEndpoints);
  private readonly workflow = inject(WorkflowState);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly events = EVENTS;
  protected readonly deviations = DEVIATIONS;
  protected readonly call = new ApiCallState();
  protected readonly endpoint = computed(() =>
    this.endpoints.url(
      this.mode() === 'event' ? this.endpoints.accessEvent : this.endpoints.accessDeviation,
    ),
  );
  protected readonly accessKeyOptions = computed(() =>
    Array.from(this.workflow.accessKeys(), ([id, value]) => ({
      id,
      label: `${String(value['displayName'] ?? value['accessType'] ?? id)} | ${String(value['itemId'] ?? '')}`,
    })),
  );

  protected manual = false;
  protected selectedId = '';
  protected accessId = '';
  protected accessKey = '';
  protected systemProvider = '';
  protected keyUserId = 'PN12345';
  protected xDeviceId = '4cf1f39b-3b2d-410b-a6c5-c726fc600da8';
  protected xHash = '3700b4d74c5348691fcd83a633fe1556';
  protected additionalInfo =
    '{"lockerUid":"77777777-7777-7777-7777-777777777777","parcelToken":"AEAAEABAqnim8zV2T5DOXLiDpwM4ifLmqFoFFEEt8anNVkNr8ZaucqMN4dD5FSH"}';
  protected reportValidationError = '';
  protected activeReport?: ReportEvent | ReportDeviation;

  protected selectAccessKey(): void {
    const selected = this.workflow.accessKeys().get(this.selectedId);
    if (!selected) {
      return;
    }
    this.applyAccessKey(selected);
  }

  protected reportEvent(event: ReportEvent): void {
    if (!this.accessId) {
      this.reportValidationError = 'Access ID or Event Type is Empty';
      return;
    }
    this.reportValidationError = '';
    this.sendReport({ event });
  }

  protected reportDeviation(deviation: ReportDeviation): void {
    if (!this.accessId) {
      this.reportValidationError = 'Access ID or Deviation message is Empty';
      return;
    }
    this.reportValidationError = '';
    this.sendReport({ deviation });
  }

  private sendReport(report: { event: ReportEvent } | { deviation: ReportDeviation }): void {
    if (this.activeReport !== undefined) {
      return;
    }

    this.activeReport = 'event' in report ? report.event : report.deviation;
    const payload = {
      keyUserId: this.keyUserId || 'PN12345',
      accessId: this.accessId,
      eventTime: new Date().toISOString(),
      ...report,
      other: this.additionalInfo,
    };

    this.call.start(payload);
    this.http
      .post(this.endpoint(), payload, {
        headers: {
          'Content-Type': 'application/json; charset=utf-8',
          'x-device-id': this.xDeviceId,
          'x-hash': this.xHash,
        },
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.activeReport = undefined;
        }),
      )
      .subscribe({
        next: () =>
          this.call.succeed(
            'event' in report
              ? { message: `${report.event} event reported` }
              : { message: 'Deviation reported' },
          ),
        error: (error: unknown) => this.call.fail(error),
      });
  }

  private applyAccessKey(value: AccessKey): void {
    this.accessId = String(value['accessId'] ?? '');
    this.accessKey = String(value['accessKey'] ?? '');
    this.systemProvider = String(value['systemProvider'] ?? '');
    this.keyUserId = String(value['keyUserId'] ?? this.keyUserId);
    this.reportValidationError = '';
    this.call.clear();
  }
}